"use client"

import React, { useEffect, useMemo, useState } from "react"
import { useRouter } from "next/navigation"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/blades/blade-dialog"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Combobox } from "@/components/ui/combobox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { ArrowRight, Loader2 } from "lucide-react"
import { toast } from "sonner"
import { useValueMappingSets } from "@/hooks/use-value-mapping-sets"
import { codeValueService } from "@/lib/code-value-service"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"
import { CodeTableValueType, type ValueMappingSet } from "@/types/fbo-api"

interface CreateValueMappingSetDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The reference data set being browsed (e.g. "job_categories", "JobOrder.status") */
  referenceType: string
}

/** "job_categories" / "JobOrder.status" → "JobCategories" / "JobOrderStatus" */
function suggestSetName(referenceType: string): string {
  return referenceType
    .split(/[^a-zA-Z0-9]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("")
}

/**
 * Creates a shared value mapping set straight from the explorer's reference data
 * browser, prefilled with the browsed reference type. When sets already reference
 * the type, they're offered for opening instead of duplicating. Saving navigates
 * to the Value Mapping Sets manager with the new set selected so value rows can
 * be added with the full editor.
 */
export function CreateValueMappingSetDialog({ open, onOpenChange, referenceType }: CreateValueMappingSetDialogProps) {
  const router = useRouter()
  const { loadSets, saveSet } = useValueMappingSets()
  const { t, n } = useTranslation()

  const [existingSets, setExistingSets] = useState<ValueMappingSet[]>([])
  const [allSets, setAllSets] = useState<ValueMappingSet[]>([])
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [valueField, setValueField] = useState("name")
  const [codeTableName, setCodeTableName] = useState<string | null>(null)
  const [codeTableValueType, setCodeTableValueType] = useState<number>(CodeTableValueType.Id)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setName(suggestSetName(referenceType))
    setDescription("")
    setValueField("name")
    setCodeTableName(null)
    setCodeTableValueType(CodeTableValueType.Id)
    setError(null)
    setIsLoading(true)
    loadSets()
      .then((sets) => {
        setAllSets(sets)
        setExistingSets(sets.filter((s) => s.atsReferenceType?.toLowerCase() === referenceType.toLowerCase()))
      })
      .catch((err) => setError(err instanceof Error ? err.message : t("explorer.valueSet.errors.load")))
      .finally(() => setIsLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, referenceType])

  const codeTableNames = useMemo(() => codeValueService.getCodeTableNames(), [])

  const openInManager = (setName: string) => {
    onOpenChange(false)
    router.push(`?view=value-mapping-sets&set=${encodeURIComponent(setName)}`)
  }

  const handleCreate = async () => {
    const trimmedName = name.trim()
    if (!trimmedName) {
      toast.error(t("explorer.valueSet.errors.nameRequired"))
      return
    }
    if (allSets.some((s) => s.name.toLowerCase() === trimmedName.toLowerCase())) {
      toast.error(t("explorer.valueSet.errors.nameTaken", { name: trimmedName }))
      return
    }

    setIsSaving(true)
    try {
      await saveSet({
        name: trimmedName,
        description,
        atsReferenceType: referenceType,
        atsReferenceValueField: valueField,
        fboCodeTableName: codeTableName,
        fboCodeTableValueType: codeTableName ? codeTableValueType : null,
        valueMappings: [],
      })
      toast.success(t("explorer.valueSet.created", { name: trimmedName }))
      openInManager(trimmedName)
    } catch (err) {
      setError(err instanceof Error ? err.message : t("explorer.valueSet.errors.create"))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("explorer.valueSet.title")}</DialogTitle>
          <DialogDescription>
            {t("explorer.valueSet.descriptionBefore")}{" "}
            <code className="bg-muted px-1 rounded">{referenceType}</code>{" "}
            {t("explorer.valueSet.descriptionAfter")}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {isLoading ? (
          <div className="flex items-center text-sm text-muted-foreground py-4">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> {t("explorer.valueSet.checkingSets")}
          </div>
        ) : (
          <div className="space-y-4">
            {existingSets.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  {t((existingSets.length === 1 ? "explorer.valueSet.existingOne" : "explorer.valueSet.existingOther"))}
                </p>
                {existingSets.map((set) => (
                  <button
                    key={set.name}
                    onClick={() => openInManager(set.name)}
                    className="w-full flex items-center justify-between rounded-md border p-3 text-left hover:bg-muted transition-colors"
                  >
                    <span>
                      <span className="font-medium text-sm">{set.name}</span>
                      <span className="text-xs text-muted-foreground ml-2">
                        {t((set.valueMappings?.length ?? 0) === 1 ? "explorer.valueSet.mappingsOne" : "explorer.valueSet.mappingsOther", {
                          count: n(set.valueMappings?.length ?? 0),
                        })}
                        {set.fboCodeTableName ? ` → ${set.fboCodeTableName}` : ""}
                      </span>
                    </span>
                    <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </button>
                ))}
                <p className="text-xs text-muted-foreground">{t("explorer.valueSet.orCreate")}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="newSetName">{t("explorer.valueSet.name")}</Label>
                <Input id="newSetName" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="newSetDescription">{t("explorer.valueSet.description")}</Label>
                <Textarea
                  id="newSetDescription"
                  rows={1}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t("explorer.valueSet.sourceValueUses")}</Label>
                <Select value={valueField} onValueChange={setValueField}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="name">{t("explorer.valueSet.sourceName")}</SelectItem>
                    <SelectItem value="id">{t("explorer.valueSet.sourceId")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t("explorer.valueSet.codeTable")}</Label>
                <Combobox
                  value={codeTableName ?? ""}
                  onValueChange={(value) => setCodeTableName(value || null)}
                  options={codeTableNames.map((table) => ({ value: table, label: table }))}
                  placeholder={t("explorer.valueSet.codeTablePlaceholder")}
                  emptyText={t("explorer.valueSet.codeTableEmpty")}
                />
              </div>
            </div>

            {codeTableName && (
              <div className="space-y-1.5 max-w-56">
                <Label>{t("explorer.valueSet.destinationValueUses")}</Label>
                <Select
                  value={String(codeTableValueType)}
                  onValueChange={(value) => setCodeTableValueType(Number(value))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={String(CodeTableValueType.Id)}>{t("explorer.valueSet.numericId")}</SelectItem>
                    <SelectItem value={String(CodeTableValueType.Code)}>{t("explorer.valueSet.stringCode")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.actions.cancel")}
          </Button>
          <Button onClick={handleCreate} disabled={isSaving || isLoading || !name.trim()}>
            {isSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {t("explorer.valueSet.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
